/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import ActionModel, { ActionType } from 'cio/models/action';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import EnvironmentService from 'cio/services/environment';
import MetricsService from 'cio/services/metrics';
import { all } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import { Entities, Periods, Measure, ALL_CHANNELS, MEASURE_LABELS } from 'cio/utils/metrics/common';
import sumBy from 'lodash.sumby';
import { AVAILABLE_WORKFLOW_MEASURE_OPTIONS, NON_RATE_MEASURES } from 'cio/utils/workflow-metrics';
import { Metric } from 'cio/utils/metrics/models/metric';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import { deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import { capitalize } from '@ember/string';
import { cached, tracked } from '@glimmer/tracking';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import { TimeRange } from 'cio/utils/time';
import Ember from 'ember';
import { PathData } from 'cio/utils/visual-workflow-builder/branch-paths';
import CampaignModel from 'cio/models/campaign';

const LABEL_MAP: Record<string, string> = {
  ...MEASURE_LABELS,
  spammed: 'spammed',
  unsubscribed: 'unsubs',
};

interface PathLabelContentArgs {
  item: {
    action: ActionModel;
  };
  edge: PathData;
  pathIndex: number;
  edgesCount: number;
  isHighlighted: boolean;
  tab: VWBTab;
  metricsTabSelectedMeasures: string[];
  metricsTabTimePeriod: Periods;
  metricsTabTimeRange: TimeRange;
}

type SummedMetric = {
  label: string;
  metric: Measure;

  totalNumeratorValue: number;
  allTimeTotalNumeratorValue: number;

  available: boolean;
  hasPercent: boolean;

  availablePathActionTypesForMetric: ActionType[];

  totalDenominatorValue: number;
  allTimeTotalDenominatorValue: number;
  percentValue: number | null;
  allTimePercentValue: number | null;

  perActionTypeTotals: ActionTypeSummedMetric;
};

type ActionTypeSummedMetric = {
  totalNumeratorValue: number;
  allTimeTotalNumeratorValue: number;
  denominator: string | null;
  totalDenominatorValue: number | null;
  allTimeTotalDenominatorValue: number | null;
  percentValue: number | null;
  allTimePercentValue: number | null;
};

export default class PathLabelContent extends Component<PathLabelContentArgs> {
  @service environment!: EnvironmentService;
  @service('metrics') metricsService!: MetricsService;

  @tracked isInViewport = false;

  get isShowingMetrics() {
    return this.args.tab === VWBTab.Metrics;
  }

  get campaign() {
    return this.args.item.action.belongsTo('campaign').value() as CampaignModel | null;
  }

  @cached
  get actionsWithMetrics() {
    let { campaign } = this;

    return this.args.edge.allNodesInPath.reduce<ActionModel[]>((acc, action) => {
      if (action.isAttributeAction || action.isManualSegmentUpdateAction) {
        return acc;
      }

      if (action.isSplitTest) {
        let variations = action.actions.toArray().map((v) => {
          let parentAction = campaign?.getConsolidatedActionParentBranch(v.id);

          if (parentAction != null) {
            return parentAction;
          }

          return v;
        });

        return [...acc, ...variations];
      }

      if (action.isMessagingType) {
        let parentAction = campaign?.getConsolidatedActionParentBranch(action.id);

        if (parentAction != null) {
          action = parentAction;
        }

        return [...acc, action];
      }

      return acc;
    }, []);
  }

  @cached
  get actionsWithMetricsPerType() {
    let { campaign } = this;

    return this.actionsWithMetrics.reduce<Partial<Record<ActionType, ActionModel[]>>>((acc, action) => {
      if (action.isMultiLangBranch) {
        let defaultAction = campaign?.getConsolidatedActionsGroupDefaultActionByBranchActionId(action.id);
        if (defaultAction == null) {
          return acc;
        }
        action = defaultAction;
      }

      if (acc[action.type] == null) {
        acc[action.type] = [];
      }

      acc[action.type] = [...(acc[action.type] as ActionModel[]), action];
      return acc;
    }, {});
  }

  @tracked metrics: Metric[] | null = null;
  hasPreparedMetrics = false;

  prepareMetrics = task({ drop: true }, async () => {
    let { campaign } = this;
    let metricsPromises = this.actionsWithMetrics.map((action) => {
      let { type, canConvert } = action;
      if (action.isMultiLangBranch) {
        let defaultAction = campaign?.getConsolidatedActionsGroupDefaultActionByBranchActionId(action.id);
        if (defaultAction != null) {
          type = defaultAction.type;
          canConvert = defaultAction.canConvert;
        }
      }

      return this.metricsService.fetchMetric(
        Entities.ACTION,
        action.id,
        this.args.metricsTabTimePeriod,
        this.args.metricsTabTimeRange,
        type,
        {
          campaignId: action.belongsTo('campaign').id(),
          environmentId: this.environment.current.get('id'),
          canConvert,
          version: 2,
        },
      );
    });

    this.metrics = await all(metricsPromises);
    this.hasPreparedMetrics = true;
  });

  @cached
  get actionTypesInPath() {
    let { campaign } = this;
    let actionTypeOrder = ALL_CHANNELS.map((c) => `${c}_action`);

    return this.actionsWithMetrics
      .map((a) => {
        if (a.isMultiLangBranch) {
          let defaultAction = campaign?.getConsolidatedActionsGroupDefaultActionByBranchActionId(a.id);
          if (defaultAction == null) {
            return null;
          }
          return defaultAction.type;
        }
        return a.type;
      })
      .uniq()
      .compact()
      .sort((a, b) => actionTypeOrder.indexOf(a) - actionTypeOrder.indexOf(b));
  }

  get summedMetrics() {
    let actionMetrics = this.metrics ?? [];

    return AVAILABLE_WORKFLOW_MEASURE_OPTIONS.map((option) => {
      let measure = option.value as Measure;

      let label = capitalize(LABEL_MAP[measure] || measure);

      if (NON_RATE_MEASURES.includes(measure)) {
        return {
          label,
          metric: measure,
          totalNumeratorValue: sumBy(actionMetrics, (metric: Metric) => metric[measure]!.total),
          allTimeTotalNumeratorValue: sumBy(actionMetrics, (metric: Metric) => metric[measure]!.allTimeTotal),
          available: true,
          hasPercent: false,
        } as SummedMetric;
      }

      let measuresPerActionTypeInPath = this.actionTypesInPath.reduce<Partial<Record<ActionType, Measure[]>>>(
        (acc, type) => {
          let actionsOfTypeInPath = this.actionsWithMetricsPerType[type] ?? [];
          let canAnyActionConvert = actionsOfTypeInPath.some((a) => a.canConvert);

          acc[type] = deliveryStatesAndActionsPerType(canAnyActionConvert)[type] as Measure[];
          return acc;
        },
        {},
      );

      let availablePathActionTypesForMetric = Object.keys(measuresPerActionTypeInPath).filter(
        (actionType: ActionType) => (measuresPerActionTypeInPath[actionType] ?? []).includes(measure),
      );

      let actionsContainingMetric = actionMetrics.filter((metric: Metric) => {
        let { actionType } = metric;

        if (!availablePathActionTypesForMetric.includes(actionType as ActionType)) {
          return false;
        }

        let measuresForType = measuresPerActionTypeInPath[actionType as ActionType];

        if (measuresForType == null) {
          return false;
        }

        return measuresForType.includes(measure);
      });

      let totalNumeratorValue = sumBy(actionsContainingMetric, (metric: Metric) => metric[measure]!.total);
      let allTimeTotalNumeratorValue = sumBy(
        actionsContainingMetric,
        (metric: Metric) => metric[measure]!.allTimeTotal,
      );

      let perActionTypeTotals = actionsContainingMetric.reduce<Partial<Record<ActionType, ActionTypeSummedMetric>>>(
        (acc, metric) => {
          if (measure === 'converted' && !metric.canConvert) {
            return acc;
          }

          let type = metric.actionType as ActionType;

          let denominator = findDenominator(measure, MeasureType.DeliveryMetrics, type);

          let data = acc[type];
          if (data == null) {
            data = {
              totalNumeratorValue: 0,
              allTimeTotalNumeratorValue: 0,
              denominator,
              totalDenominatorValue: denominator == null ? null : 0,
              allTimeTotalDenominatorValue: denominator == null ? null : 0,
              percentValue: null,
              allTimePercentValue: null,
            };
          }

          data.totalNumeratorValue += metric[measure]!.total;
          data.allTimeTotalNumeratorValue += metric[measure]!.allTimeTotal;
          if (denominator != null) {
            if (data.totalDenominatorValue == null) {
              data.totalDenominatorValue = metric[denominator]!.total;
            } else {
              data.totalDenominatorValue += metric[denominator]!.total ?? 0;
            }
            if (data.allTimeTotalDenominatorValue == null) {
              data.allTimeTotalDenominatorValue = metric[denominator]!.allTimeTotal;
            } else {
              data.allTimeTotalDenominatorValue += metric[denominator]!.allTimeTotal ?? 0;
            }

            if (data.totalDenominatorValue) {
              data.percentValue = data.totalNumeratorValue / data.totalDenominatorValue;
            }
            if (data.allTimeTotalDenominatorValue) {
              data.allTimePercentValue = data.allTimeTotalNumeratorValue / data.allTimeTotalDenominatorValue;
            }
          }

          acc[type] = data;
          return acc;
        },
        {},
      );

      let totalDenominatorValue = Object.values(perActionTypeTotals).reduce<number | null>(
        (acc, m: ActionTypeSummedMetric) => {
          if (acc == null) {
            return m.totalDenominatorValue;
          }

          if (m.totalDenominatorValue != null) {
            return acc + m.totalDenominatorValue;
          }

          return acc;
        },
        null,
      );
      let allTimeTotalDenominatorValue = Object.values(perActionTypeTotals).reduce<number | null>(
        (acc, m: ActionTypeSummedMetric) => {
          if (acc == null) {
            return m.allTimeTotalDenominatorValue;
          }

          if (m.allTimeTotalDenominatorValue != null) {
            return acc + m.allTimeTotalDenominatorValue;
          }

          return acc;
        },
        null,
      );

      return {
        label,
        metric: measure,
        availablePathActionTypesForMetric,
        totalDenominatorValue,
        allTimeTotalDenominatorValue,
        totalNumeratorValue,
        allTimeTotalNumeratorValue,
        percentValue:
          totalDenominatorValue != null && totalDenominatorValue !== 0
            ? totalNumeratorValue / totalDenominatorValue
            : null,
        allTimePercentValue:
          allTimeTotalDenominatorValue != null && allTimeTotalDenominatorValue !== 0
            ? allTimeTotalNumeratorValue / allTimeTotalDenominatorValue
            : null,
        hasPercent: true,
        available: actionsContainingMetric.length > 0,
        perActionTypeTotals,
      } as SummedMetric;
    }).reduce<Partial<Record<Measure, SummedMetric>>>((acc, item) => {
      acc[item.metric] = item;
      return acc;
    }, {});
  }

  @cached
  get metricData() {
    let columns = this.args.metricsTabSelectedMeasures;
    let data = this.summedMetrics;

    return columns.map((measure: Measure) => data[measure]);
  }

  constructor(owner: unknown, args: PathLabelContentArgs) {
    super(owner, args);

    if (Ember.testing) {
      this.isInViewport = true;

      if (this.isShowingMetrics) {
        this.prepareMetrics.perform();
      }
    }
  }

  @action
  onPeriodOrRangeChange() {
    this.hasPreparedMetrics = false;

    if (this.isShowingMetrics && this.isInViewport) {
      this.prepareMetrics.perform();
    }
  }

  @action
  isShowingMetricsChanged() {
    if (!this.isShowingMetrics) {
      this.hasPreparedMetrics = false;
      return;
    }

    if (this.isInViewport) {
      this.prepareMetrics.perform();
    }
  }

  @action
  onEnterViewport() {
    if (Ember.testing) {
      return;
    }

    this.isInViewport = true;

    // No need to re-fetch metrics if they've already been retrieved previously.
    // Otherwise we'll keep fetching metrics every time the user scrolls to
    // reveal a metrics panel, which isn't necessary.
    if (this.isShowingMetrics && !this.hasPreparedMetrics) {
      this.prepareMetrics.perform();
    }
  }

  @action
  onExitViewport() {
    if (Ember.testing) {
      return;
    }

    this.isInViewport = false;
  }
}
